import { ConversationParticipantEntity } from '@provision/conversation-types';
import { MouseEvent, useCallback, useMemo } from 'react';
import { type IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import {
  Address,
  Conversation,
  useAddress,
  useAddressDelete,
  useGroupRules,
  UserGroupRules,
} from 'src/graphql';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { useConversationAssociatedSites } from 'src/pages/Conversations/SidePanel/ConversationDetails/ConversationSite/use-conversation-associated-sites';
import { Modals, useShowModals } from 'src/store/modals';
import {
  useConfirmationModal,
  useCopyToClipboard,
  useEntityFullUrl,
  useGetTosUrl,
  useOpenSite,
} from 'src/utils/hooks';

const LUCIDE_ICON_LIBRARY: IconLibrary = 'lucide';

export const useSiteActions = ({
  address,
  conversation,
}: {
  address: Address;
  conversation?: Conversation;
}) => {
  const { tosUrl } = useGetTosUrl({
    partnerId: address?.partnerId,
    brandId: address?.brandId,
  });
  const { copyToClipboard } = useCopyToClipboard();

  const { groupRules } = useGroupRules();

  const { confirm } = useConfirmationModal();

  const openSite = useOpenSite();
  const { deleteAddress } = useAddressDelete();

  const { createConversationToAddress, createClientToAddress, deleteConversationToAddress } =
    useAddress(address?.addressId);
  const [showUpdateSiteModal, showConversationToAddressModal, showMergeSiteModal] =
    useShowModals(
      Modals.AddSiteModal,
      Modals.UpdateConversationToAddress,
      Modals.MergeSiteModal,
    );

  const { updateAssociatedSitesCache } = useConversationAssociatedSites(conversation);

  const requesterEntity = conversation?.requester
    ?.participantEntity as ConversationParticipantEntity;

  const onSelectSite = useCallback(
    async ({
      clientAddress,
      siteToAdd,
      filters,
    }: {
      clientAddress?: Address;
      siteToAdd?: Address;
      filters?: ConditionInput[];
    }) => {
      if (conversation?.conversationId) {
        await deleteConversationToAddress(
          conversation?.conversationId,
          conversation?.address?.partnerId,
        );
      }

      if (requesterEntity === ConversationParticipantEntity.Client && siteToAdd) {
        await createClientToAddress({
          client: {
            ...(conversation?.requester?.client ?? {}),
            id:
              conversation?.requester?.client.clientId ??
              conversation?.requester?.participantIdentity,
            clientId:
              conversation?.requester?.client.clientId ??
              conversation?.requester?.participantIdentity,
          },
          address: siteToAdd,
          partnerId: conversation?.partner?.partnerId,
          filters,
        });
        updateAssociatedSitesCache?.(siteToAdd);
      }

      if (conversation?.conversationId) {
        await createConversationToAddress(
          conversation?.conversationId,
          (clientAddress || siteToAdd)!,
          conversation?.partnerId,
        );
      }
    },
    [
      updateAssociatedSitesCache,
      conversation?.address?.partnerId,
      conversation?.conversationId,
      conversation?.partnerId,
      conversation?.partner?.partnerId,
      conversation?.requester?.client,
      conversation?.requester?.participantIdentity,
      createClientToAddress,
      createConversationToAddress,
      deleteConversationToAddress,
      requesterEntity,
    ],
  );

  const siteFullUrl = useEntityFullUrl(
    address?.partnerId,
    EntityNames.Site,
    address?.addressId,
  );

  const isDeletedAddress = !!address.isDeleted;

  const accessToMergeSites =
    !!groupRules[UserGroupRules.AccessToMergeSites] && !isDeletedAddress;

  const disabled = conversation?.conversationStatusId === 'Closed';

  const canDeleteAddress =
    groupRules[UserGroupRules.AccessToDeleteAddresses] && !isDeletedAddress;

  return useMemo(
    () => ({
      openSite: {
        label: 'Open site',
        icon: 'home',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        // only where the site is an associated object; on the Site page itself
        // the viewer is already there
        isAllowed: !!address?.id && !!conversation?.conversationId,
        // the menu always supplies the event, and openSite needs it to honour
        // shift / cmd for new tab / new global tab
        handler: (event?: MouseEvent<HTMLElement>) => {
          if (!event) {
            return;
          }
          openSite(event, address);
        },
      },
      updateSite: {
        label: 'Update site',
        icon: 'map-pin',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !isDeletedAddress,
        handler: () => {
          showUpdateSiteModal({ address });
        },
      },
      copySiteId: {
        label: 'Copy Site ID',
        icon: 'copy',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        handler: async () => {
          await copyToClipboard(address?.addressId);
        },
      },
      copySiteUrl: {
        label: 'Copy Site URL',
        icon: 'link',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !!siteFullUrl,
        handler: async () => {
          await copyToClipboard(siteFullUrl);
        },
      },
      copyTOS: {
        label: 'Copy Terms of Service URL',
        icon: 'link',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !!tosUrl,
        handler: async () => {
          await copyToClipboard(tosUrl);
        },
      },
      mergeSite: {
        label: 'Merge Site',
        icon: 'merge',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: accessToMergeSites,
        handler: () => {
          showMergeSiteModal({ address });
        },
      },
      changeSites: {
        label: 'Change sites',
        icon: 'arrow-left-right',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !disabled && !!conversation?.conversationId && !isDeletedAddress,
        handler: () => {
          if (conversation?.conversationId) {
            showConversationToAddressModal({
              conversation,
              address,
              onSelect: (addressId, { clientAddress, siteToAdd, filters }) => {
                if (!addressId || addressId === conversation?.address?.addressId) return;
                const newValue = clientAddress || siteToAdd;

                if (!newValue) return;
                void onSelectSite({ clientAddress, siteToAdd, filters });
              },
              onNewSiteAdded: (newValue) => {
                void onSelectSite({ siteToAdd: newValue });
                updateAssociatedSitesCache(newValue);
              },
            });
          }
        },
      },
      disassociateSite: {
        label: 'Disassociate Site',
        icon: 'unlink',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !disabled && !!conversation?.conversationId && !isDeletedAddress,
        handler: async () => {
          if (conversation?.conversationId) {
            await deleteConversationToAddress(
              conversation.conversationId,
              conversation?.requester?.partnerId,
            );
          }
        },
      },
      deleteSite: {
        label: 'Delete site',
        icon: 'trash-2',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: canDeleteAddress,
        handler: async () => {
          if (
            await confirm({
              body: 'Please confirm you want to delete the Site and all related data.',
              variant: 'delete',
            })
          ) {
            await deleteAddress(address.addressId);
          }
        },
      },
    }),
    [
      isDeletedAddress,
      siteFullUrl,
      tosUrl,
      accessToMergeSites,
      disabled,
      conversation,
      canDeleteAddress,
      showUpdateSiteModal,
      address,
      copyToClipboard,
      showMergeSiteModal,
      showConversationToAddressModal,
      onSelectSite,
      updateAssociatedSitesCache,
      deleteConversationToAddress,
      confirm,
      deleteAddress,
      openSite,
    ],
  );
};
